/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `userFormValidation` for the Caintra Connect frontend.
 * @module utils/userFormValidation
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import {
  validateEmail,
  validateMobilePhoneNumber,
  validateRFC,
} from "./common.js";

/**
 * Validates user form email, phone, and RFC fields.
 * @param {{ correoElectronico: string, numeroCelular: string, RFC: string }} fields
 * @returns {{ valid: boolean, emailFormatError: string|null, phoneFormatError: string|null, rfcFormatError: string|null }}
 */
export const validateUserFormFields = ({
  correoElectronico,
  numeroCelular,
  RFC,
}) => {
  let valid = true;
  let emailFormatError = null;
  let phoneFormatError = null;
  let rfcFormatError = null;

  if (correoElectronico.trim() !== "") {
    const emailResult = validateEmail(correoElectronico);
    if (!emailResult.isValid) {
      emailFormatError = emailResult.error;
      valid = false;
    }
  }

  if (numeroCelular.trim() !== "") {
    const phoneResult = validateMobilePhoneNumber(numeroCelular);
    if (!phoneResult.isValid) {
      phoneFormatError = phoneResult.error;
      valid = false;
    }
  }

  if (RFC.trim() !== "") {
    const rfcResult = validateRFC(RFC);
    if (!rfcResult.isValid) {
      rfcFormatError = rfcResult.error;
      valid = false;
    }
  }

  return {
    valid,
    emailFormatError,
    phoneFormatError,
    rfcFormatError,
  };
};
